import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Dictado por voz usando la Web Speech API nativa del navegador.
 * No sube audio a ningún servidor: la transcripción la hace el propio navegador.
 *
 * Soportado en Chrome, Edge y Safari. Firefox no la implementa, por eso se
 * expone isSupported para poder esconder el botón en vez de mostrar algo roto.
 *
 * El texto final lo entrega por onResult en vez de acumularlo acá: así el
 * componente que lo usa sigue siendo el dueño de su textarea y decide dónde
 * insertarlo (respetando el cursor, lo ya escrito, etc.).
 */
// Locales de español que el reconocimiento de voz acepta. Importa porque
// navigator.language suele devolver 'es-419' (español de Latinoamérica), que NO
// está entre ellos: pasarlo tal cual hace que el motor caiga en su idioma por
// defecto y transcriba en inglés.
const SPANISH_LOCALES = new Set([
    'es-AR', 'es-BO', 'es-CL', 'es-CO', 'es-CR', 'es-DO', 'es-EC', 'es-SV',
    'es-GT', 'es-HN', 'es-MX', 'es-NI', 'es-PA', 'es-PY', 'es-PE', 'es-PR',
    'es-ES', 'es-US', 'es-UY', 'es-VE',
]);

function resolveSpanishLocale(preferred) {
    if (preferred) return preferred;

    const browserLang = typeof navigator !== 'undefined' ? navigator.language : null;
    if (browserLang && SPANISH_LOCALES.has(browserLang)) return browserLang;

    // 'es', 'es-419' o cualquier otro idioma: se dicta en español neutro.
    return 'es-ES';
}

export function useSpeechToText({ lang, onResult } = {}) {
    const [isListening, setIsListening] = useState(false);
    const [interimText, setInterimText] = useState('');
    const [error, setError] = useState(null);

    const recognitionRef = useRef(null);
    const shouldListenRef = useRef(false);   // lo que el usuario quiere, vs lo que el motor hace
    const onResultRef = useRef(onResult);

    // Se guarda en una ref para que recrear el callback en el componente no
    // obligue a reconstruir el reconocedor a mitad de un dictado.
    useEffect(() => { onResultRef.current = onResult; }, [onResult]);

    const SpeechRecognition = typeof window !== 'undefined'
        ? (window.SpeechRecognition || window.webkitSpeechRecognition)
        : undefined;

    const isSupported = !!SpeechRecognition;

    // Se respeta la variante regional del navegador (es-VE, es-MX...) sólo si el
    // motor la soporta; si no, español neutro.
    const resolvedLang = resolveSpanishLocale(lang);

    const stop = useCallback(() => {
        shouldListenRef.current = false;
        setInterimText('');
        try {
            recognitionRef.current?.stop();
        } catch {
            // stop() lanza si ya estaba detenido; no aporta nada al usuario.
        }
        setIsListening(false);
    }, []);

    const start = useCallback(() => {
        if (!isSupported || shouldListenRef.current) return;

        setError(null);
        shouldListenRef.current = true;

        const recognition = new SpeechRecognition();
        recognition.lang = resolvedLang;
        recognition.continuous = true;      // no cortar en la primera pausa
        recognition.interimResults = true;  // texto en vivo mientras se habla

        recognition.onresult = (event) => {
            let interim = '';
            let final = '';

            // Sólo lo nuevo desde resultIndex: lo anterior ya se entregó.
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const chunk = event.results[i][0].transcript;
                if (event.results[i].isFinal) final += chunk;
                else interim += chunk;
            }

            setInterimText(interim);
            if (final.trim()) onResultRef.current?.(final.trim());
        };

        recognition.onerror = (event) => {
            if (event.error === 'no-speech' || event.error === 'aborted') return; // ruido normal
            shouldListenRef.current = false;
            setIsListening(false);
            setInterimText('');
            setError(
                event.error === 'not-allowed'
                    ? 'Permiso de micrófono denegado. Habilitalo en el navegador para dictar.'
                    : 'No se pudo iniciar el dictado. Revisá el micrófono e intentá de nuevo.'
            );
        };

        recognition.onend = () => {
            // Chrome corta solo tras unos segundos de silencio. Si el usuario no
            // apretó "parar", se reanuda para que el dictado se sienta continuo.
            if (shouldListenRef.current) {
                try {
                    recognition.start();
                    return;
                } catch {
                    // No se pudo reanudar: hay que bajar la bandera además del
                    // estado, si no toggle() creeria que sigue activo y el
                    // siguiente clic intentaria detener algo ya detenido.
                    shouldListenRef.current = false;
                }
            }
            setIsListening(false);
            setInterimText('');
        };

        try {
            recognition.start();
            recognitionRef.current = recognition;
            setIsListening(true);
        } catch {
            shouldListenRef.current = false;
            setError('No se pudo iniciar el dictado.');
        }
    }, [isSupported, resolvedLang, SpeechRecognition]);

    const toggle = useCallback(() => {
        if (shouldListenRef.current) stop();
        else start();
    }, [start, stop]);

    // Cortar el micrófono si el componente se desmonta en pleno dictado.
    useEffect(() => {
        return () => {
            shouldListenRef.current = false;
            try { recognitionRef.current?.stop(); } catch { /* ya detenido */ }
        };
    }, []);

    return { isSupported, isListening, interimText, error, start, stop, toggle };
}

/**
 * Inserta el texto dictado en lo que ya había escrito, cuidando el espaciado
 * para que no queden palabras pegadas ni espacios de más.
 */
export function appendDictated(current, addition) {
    if (!addition) return current;
    if (!current) return addition;
    return /\s$/.test(current) ? current + addition : `${current} ${addition}`;
}
